import { DatesSetArg } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  CalendarViewType,
  getComplianceCalendarNow,
} from '../helpers/complianceCalendarHelpers';
import {
  getListRangeDayCount,
  getListRangeFromAnchor,
  ListRangeDirection,
} from '../helpers/complianceCalendarListViewHelpers';
import {
  getMonthRangeContaining,
  getTitle,
  getWeekRangeContaining,
  shiftUtcDays,
  shiftUtcYears,
  WeekRangeDirection,
  WEEK_VIEW_DAY_COUNT,
} from '../helpers/complianceCalendarWeekViewHelpers';

type UseComplianceCalendarNavigationParams = {
  setView: (view: CalendarViewType) => void;
  view: CalendarViewType;
};

function getVisibleRangeAroundToday(view: CalendarViewType) {
  const today = getComplianceCalendarNow();

  if (view === CalendarViewType.Week) {
    return getWeekRangeContaining(today);
  }
  if (view === CalendarViewType.List) {
    return getListRangeFromAnchor(today);
  }

  return getMonthRangeContaining(today);
}

export function useComplianceCalendarNavigation({
  setView,
  view,
}: UseComplianceCalendarNavigationParams) {
  const calendarRef = useRef<FullCalendar>(null);

  const [title, setTitle] = useState(() => {
    const { start, exclusiveEnd } = getVisibleRangeAroundToday(view);
    return getTitle(start, exclusiveEnd);
  });
  const [visibleRangeStart, setVisibleRangeStart] = useState(
    () => getVisibleRangeAroundToday(view).start,
  );
  const [visibleRangeExclusiveEnd, setVisibleRangeExclusiveEnd] = useState(
    () => getVisibleRangeAroundToday(view).exclusiveEnd,
  );
  // the month to return to from week view, so a week overlapping two months
  // doesn't snap month view back to a different one than the user was on
  const [savedMonthStart, setSavedMonthStart] = useState(
    () => getMonthRangeContaining(getComplianceCalendarNow()).start,
  );

  const isWeekView = view === CalendarViewType.Week;
  const isListView = view === CalendarViewType.List;

  const onDatesSet = useCallback((info: DatesSetArg) => {
    setVisibleRangeStart(info.start);
    setVisibleRangeExclusiveEnd(info.end);
    setTitle(info.view.title);
    setSavedMonthStart(info.view.currentStart);
  }, []);

  const updateVisibleRange = useCallback((start: Date, exclusiveEnd: Date) => {
    setVisibleRangeStart(start);
    setVisibleRangeExclusiveEnd(exclusiveEnd);
    setTitle(getTitle(start, exclusiveEnd));
  }, []);

  const updateWeekRange = useCallback(
    ({
      start,
      exclusiveEnd,
      preferredMonthDate,
    }: {
      start: Date;
      exclusiveEnd: Date;
      preferredMonthDate?: Date;
    }) => {
      updateVisibleRange(start, exclusiveEnd);
      const endMonthStart = getMonthRangeContaining(
        shiftUtcDays(exclusiveEnd, -1),
      ).start;

      // a week overlapping two months keeps the preferred (default: saved)
      // month if it's one of them
      setSavedMonthStart((saved) => {
        const preferred = preferredMonthDate
          ? getMonthRangeContaining(preferredMonthDate).start
          : saved;

        // only use the end of the range's month if it's the preferred month
        return preferred.getTime() === endMonthStart.getTime()
          ? preferred
          : getMonthRangeContaining(start).start;
      });
    },
    [updateVisibleRange],
  );

  const handleViewChange = useCallback(
    (newView: CalendarViewType) => {
      // Prefer today when it's still on screen so week/list don't jump to the 1st
      const today = getComplianceCalendarNow();
      const todayIsInRange =
        visibleRangeStart <= today && today < visibleRangeExclusiveEnd;
      const anchor = todayIsInRange ? today : visibleRangeStart;

      if (newView === CalendarViewType.Week) {
        const { start, exclusiveEnd } = getWeekRangeContaining(anchor);
        updateWeekRange({ start, exclusiveEnd, preferredMonthDate: anchor });
        setView(CalendarViewType.Week);
        return;
      }
      if (newView === CalendarViewType.List) {
        const { start, exclusiveEnd } = getListRangeFromAnchor(anchor);
        updateVisibleRange(start, exclusiveEnd);
        setView(CalendarViewType.List);
        return;
      }

      // default to month view
      const { start, exclusiveEnd } = getMonthRangeContaining(
        view === CalendarViewType.Week ? savedMonthStart : visibleRangeStart,
      );
      updateVisibleRange(start, exclusiveEnd);
      setView(CalendarViewType.Month);
    },
    [
      savedMonthStart,
      setView,
      updateVisibleRange,
      updateWeekRange,
      view,
      visibleRangeExclusiveEnd,
      visibleRangeStart,
    ],
  );

  const shiftWeekRange = useCallback(
    (direction: WeekRangeDirection) => {
      const days =
        direction === WeekRangeDirection.Next
          ? WEEK_VIEW_DAY_COUNT
          : -WEEK_VIEW_DAY_COUNT;

      updateWeekRange({
        start: shiftUtcDays(visibleRangeStart, days),
        exclusiveEnd: shiftUtcDays(visibleRangeExclusiveEnd, days),
      });
    },
    [updateWeekRange, visibleRangeExclusiveEnd, visibleRangeStart],
  );

  const shiftListRange = useCallback(
    (direction: ListRangeDirection) => {
      const dayCount = getListRangeDayCount(
        visibleRangeStart,
        visibleRangeExclusiveEnd,
      );
      const days = direction === ListRangeDirection.Next ? dayCount : -dayCount;

      updateVisibleRange(
        shiftUtcDays(visibleRangeStart, days),
        shiftUtcDays(visibleRangeExclusiveEnd, days),
      );
    },
    [updateVisibleRange, visibleRangeExclusiveEnd, visibleRangeStart],
  );

  const shiftListRangeByYears = useCallback(
    (years: number) => {
      const start = shiftUtcYears(visibleRangeStart, years);
      const exclusiveEnd = shiftUtcDays(
        start,
        getListRangeDayCount(visibleRangeStart, visibleRangeExclusiveEnd),
      );
      updateVisibleRange(start, exclusiveEnd);
    },
    [updateVisibleRange, visibleRangeExclusiveEnd, visibleRangeStart],
  );

  const goToToday = useCallback(() => {
    if (isWeekView) {
      const today = getComplianceCalendarNow();
      const { start, exclusiveEnd } = getWeekRangeContaining(today);
      updateWeekRange({ start, exclusiveEnd, preferredMonthDate: today });
      return;
    }

    if (isListView) {
      const start = getComplianceCalendarNow();
      start.setUTCHours(0, 0, 0, 0);
      const exclusiveEnd = shiftUtcDays(
        start,
        getListRangeDayCount(visibleRangeStart, visibleRangeExclusiveEnd),
      );
      updateVisibleRange(start, exclusiveEnd);
      return;
    }

    calendarRef.current?.getApi()?.today();
  }, [
    isListView,
    isWeekView,
    updateVisibleRange,
    updateWeekRange,
    visibleRangeExclusiveEnd,
    visibleRangeStart,
  ]);

  const goToPrevious = useCallback(() => {
    if (isWeekView) {
      shiftWeekRange(WeekRangeDirection.Previous);
      return;
    }

    if (isListView) {
      shiftListRange(ListRangeDirection.Previous);
      return;
    }

    calendarRef.current?.getApi()?.prev();
  }, [isListView, isWeekView, shiftListRange, shiftWeekRange]);

  const goToNext = useCallback(() => {
    if (isWeekView) {
      shiftWeekRange(WeekRangeDirection.Next);
      return;
    }

    if (isListView) {
      shiftListRange(ListRangeDirection.Next);
      return;
    }

    calendarRef.current?.getApi()?.next();
  }, [isListView, isWeekView, shiftListRange, shiftWeekRange]);

  const goToPreviousYear = useCallback(() => {
    if (isWeekView) {
      const { start, exclusiveEnd } = getWeekRangeContaining(
        shiftUtcYears(visibleRangeStart, -1),
      );
      updateWeekRange({
        start,
        exclusiveEnd,
        preferredMonthDate: shiftUtcYears(savedMonthStart, -1),
      });
      return;
    }

    if (isListView) {
      shiftListRangeByYears(-1);
      return;
    }

    calendarRef.current?.getApi()?.prevYear();
  }, [
    isListView,
    isWeekView,
    savedMonthStart,
    shiftListRangeByYears,
    updateWeekRange,
    visibleRangeStart,
  ]);

  const goToNextYear = useCallback(() => {
    if (isWeekView) {
      const { start, exclusiveEnd } = getWeekRangeContaining(
        shiftUtcYears(visibleRangeStart, 1),
      );
      updateWeekRange({
        start,
        exclusiveEnd,
        preferredMonthDate: shiftUtcYears(savedMonthStart, 1),
      });
      return;
    }

    if (isListView) {
      shiftListRangeByYears(1);
      return;
    }

    calendarRef.current?.getApi()?.nextYear();
  }, [
    isListView,
    isWeekView,
    savedMonthStart,
    shiftListRangeByYears,
    updateWeekRange,
    visibleRangeStart,
  ]);

  return useMemo(
    () => ({
      calendarRef,
      goToNext,
      goToNextYear,
      goToPrevious,
      goToPreviousYear,
      goToToday,
      handleViewChange,
      isWeekView,
      onDatesSet,
      title,
      updateVisibleRange,
      view,
      visibleRangeExclusiveEnd,
      visibleRangeStart,
    }),
    [
      goToNext,
      goToNextYear,
      goToPrevious,
      goToPreviousYear,
      goToToday,
      handleViewChange,
      isWeekView,
      onDatesSet,
      title,
      updateVisibleRange,
      view,
      visibleRangeExclusiveEnd,
      visibleRangeStart,
    ],
  );
}
